import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { AdminChannelHealthState, AdminChannelOptOutSource } from '../../__generated__/globalTypes';
import Table from '../../components/Table';

import {
  CHANNEL_NAMES,
  channelHealthBadge,
  formatLatency,
  formatShare,
  membersInState,
  membersOptedOutBy,
  MIN_SAMPLE_SIZE,
  optOutSourceApplies,
  optOutSourceLabel,
  sentenceCase,
} from './channelHealthView';

import type { AdminGetChannelHealthQuery } from './__generated__/ChannelHealthScene.gql';

type ChannelSummary = AdminGetChannelHealthQuery['adminGetChannelHealth']['channels'][number];
type OutreachLatency = ChannelSummary['receipts']['sendLatency'];

interface IMetricRow {
  label: string;
  /** What the row counts, on hover: the labels leave the unit and the edge cases out. */
  definition: string;
  /** A breakdown of the row above it. */
  isBreakdown?: boolean;
  render: (summary: ChannelSummary) => React.ReactNode;
}

interface IMetricSection {
  title: string;
  definition: string;
  rows: IMetricRow[];
}

const NotApplicable = () => <span className="text-fg-quaternary">—</span>;

interface ICountWithShareProps {
  count: number;
  total: number;
}

/** A count, with its share of `total` once there are enough to make one. */
const CountWithShare = ({ count, total }: ICountWithShareProps) => {
  const share = formatShare(count, total);
  return (
    <>
      {count}
      {share !== null && <span className="text-fg-tertiary ml-1.5 inline-block w-9 text-right">{share}</span>}
    </>
  );
};

interface ILatencyProps {
  latency: OutreachLatency;
}

/** A latency's p50 / p90 beside how many openings it measured; the percentiles wait for enough of them. */
const Latency = ({ latency }: ILatencyProps) => {
  const percentiles = formatLatency(latency);
  return (
    <>
      {percentiles ?? <span className="text-fg-quaternary">—</span>}
      <span className="text-fg-tertiary ml-1.5">n={latency.measured}</span>
    </>
  );
};

const STATE_DEFINITIONS: Record<AdminChannelHealthState, string> = {
  [AdminChannelHealthState.Healthy]: 'No unresolved delivery failure.',
  [AdminChannelHealthState.Failing]: 'Open, but the last send failed and nothing has been delivered since.',
  [AdminChannelHealthState.Paused]: 'A failed send holds outreach here for a day, or until a later delivery.',
  [AdminChannelHealthState.Undeliverable]: 'The provider reported the number or address cannot take messages.',
  [AdminChannelHealthState.OptedOut]: 'Out of proactive messages, by the member, a spam report, or a carrier STOP.',
  [AdminChannelHealthState.Unverified]: 'No verified number or address, so nothing is sent.',
};

function stateRow(state: AdminChannelHealthState): IMetricRow {
  return {
    label: sentenceCase(channelHealthBadge(state).label),
    definition: STATE_DEFINITIONS[state],
    render: (summary) => membersInState(summary.states, state),
  };
}

const OPT_OUT_SOURCE_DEFINITIONS: Record<AdminChannelOptOutSource, string> = {
  [AdminChannelOptOutSource.Member]: 'The member left proactive messages on this channel.',
  [AdminChannelOptOutSource.SpamReport]: 'A spam report opted the address out. Email only.',
  [AdminChannelOptOutSource.CarrierStop]: 'The number texted STOP. Texts only.',
};

function optOutSourceRow(source: AdminChannelOptOutSource): IMetricRow {
  return {
    label: optOutSourceLabel(source),
    definition: OPT_OUT_SOURCE_DEFINITIONS[source],
    isBreakdown: true,
    render: (summary) =>
      optOutSourceApplies(summary.channel, source) ? (
        membersOptedOutBy(summary.optOutSources, source)
      ) : (
        <NotApplicable />
      ),
  };
}

const SECTIONS: IMetricSection[] = [
  {
    title: 'Members now',
    definition: "Every active member's channel as it stands at this read.",
    rows: [
      stateRow(AdminChannelHealthState.Healthy),
      stateRow(AdminChannelHealthState.Failing),
      stateRow(AdminChannelHealthState.Paused),
      stateRow(AdminChannelHealthState.Undeliverable),
      stateRow(AdminChannelHealthState.OptedOut),
      optOutSourceRow(AdminChannelOptOutSource.Member),
      optOutSourceRow(AdminChannelOptOutSource.SpamReport),
      optOutSourceRow(AdminChannelOptOutSource.CarrierStop),
      stateRow(AdminChannelHealthState.Unverified),
    ],
  },
  {
    title: 'Members in the window',
    definition: 'Active members with at least one such event since the window opened, from stamps every send moves.',
    rows: [
      {
        label: 'With a delivery',
        definition: "At least one delivery confirmed. For texts, from the assistant's own number only.",
        render: (summary) => summary.membersWithDelivery,
      },
      {
        label: 'With a failure',
        definition: "At least one failed send or bounce. For texts, from the assistant's own number only.",
        render: (summary) => summary.membersWithFailure,
      },
      {
        label: 'With a spam report',
        definition: 'Email only: the address sent a spam report.',
        render: (summary) => summary.membersWithSpamReport ?? <NotApplicable />,
      },
    ],
  },
  {
    title: 'Outreach openings in the window',
    definition:
      'One per conversation an outreach pass opened, by what its provider receipts said. Openings and members are different units.',
    rows: [
      {
        label: 'Sent',
        definition: 'Openings put on the wire since the window opened.',
        render: (summary) => summary.receipts.sent,
      },
      {
        label: 'Delivered',
        definition: 'A receipt confirmed a message of the opening, whatever else was reported.',
        render: ({ receipts }) => <CountWithShare count={receipts.delivered} total={receipts.sent} />,
      },
      {
        label: 'Failed',
        definition: 'A receipt reported a failure and none confirmed a message.',
        render: ({ receipts }) => <CountWithShare count={receipts.failed} total={receipts.sent} />,
      },
      {
        label: 're-armed',
        definition: 'Failed openings whose work the outreach sweep has offered again.',
        isBreakdown: true,
        render: (summary) => summary.receipts.rearmed,
      },
      {
        label: 'No receipt',
        definition: 'No receipt has said either way yet.',
        render: ({ receipts }) => <CountWithShare count={receipts.unreported} total={receipts.sent} />,
      },
      {
        label: 'Send latency p50 / p90',
        definition: 'From the hand-off to the message going out: the composing turn, and any wait for it.',
        render: (summary) => <Latency latency={summary.receipts.sendLatency} />,
      },
      {
        label: 'Delivery latency p50 / p90',
        definition:
          "From the send to the delivery receipt's arrival, the provider's and carrier's reporting delay included. Only openings confirmed since receipts began recording their send time.",
        render: (summary) => <Latency latency={summary.receipts.deliveryLatency} />,
      },
    ],
  },
];

interface IChannelMetricsTableProps {
  channels: ChannelSummary[];
}

/**
 * Every fleet count the report holds, one row per measure and one column per
 * channel, so text and email read side by side. Hovering a row says what it
 * counts. Rates and percentiles wait for {@link MIN_SAMPLE_SIZE} openings.
 */
const ChannelMetricsTable = ({ channels }: IChannelMetricsTableProps) => (
  <section className="flex flex-col gap-2">
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell />
          {channels.map((summary) => (
            <Table.HeaderCell key={summary.channel} className="w-40 text-right">
              {CHANNEL_NAMES[summary.channel]}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {SECTIONS.map((section) => (
          <React.Fragment key={section.title}>
            <Table.Row className="hover:bg-transparent">
              <Table.Cell
                colSpan={channels.length + 1}
                title={section.definition}
                className="text-fg-secondary pt-3 text-[10px] font-semibold uppercase tracking-wider"
              >
                {section.title}
              </Table.Cell>
            </Table.Row>
            {section.rows.map((row) => (
              <Table.Row key={row.label}>
                <Table.Cell
                  title={row.definition}
                  className={cx('text-xs', row.isBreakdown && 'text-fg-tertiary pl-7')}
                >
                  {row.label}
                </Table.Cell>
                {channels.map((summary) => (
                  <Table.Cell key={summary.channel} className="whitespace-nowrap text-right text-xs">
                    {row.render(summary)}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </React.Fragment>
        ))}
      </Table.Body>
    </Table>
    <span className="text-fg-tertiary text-xs">
      Rates and percentiles show from {MIN_SAMPLE_SIZE} openings. Hover a row for what it counts.
    </span>
  </section>
);

export default ChannelMetricsTable;
